import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";

const theme = createTheme({
  palette: {
    primary: { main: "#1C2541" },
    secondary: { main: "#EE8013" },
  },
});

const Blog = () => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const lang = i18n.language?.split("-")[0] || "en";

  useEffect(() => {
    fetch(`https://api.whynotcarrental.com/api/blog`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setPosts(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const getContent = (post) => {
    const c = post[lang];
    if (c?.title) return c;
    return post.en || {};
  };

  const getCoverUrl = (post) =>
    post.coverImage
      ? `https://api.whynotcarrental.com/uploads/blog/${post.coverImage}`
      : null;

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <ThemeProvider theme={theme}>
      {/* Hero */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1C2541 0%, #202A67 100%)",
          color: "white",
          py: { xs: 7, md: 11 },
          textAlign: "center",
          px: 2,
        }}
      >
        <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
          Blog
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.75, maxWidth: 560, mx: "auto", fontWeight: 400 }}>
          {t("blogSubtitle", "News, guides and tips for your trip to Madeira")}
        </Typography>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 9 } }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={12}>
            <CircularProgress sx={{ color: "#1C2541" }} />
          </Box>
        ) : posts.length === 0 ? (
          <Typography textAlign="center" color="text.secondary" py={10} variant="h6">
            {t("noPosts", "No articles published yet. Check back soon!")}
          </Typography>
        ) : (
          <Grid container spacing={4}>
            {posts.map((post) => {
              const content = getContent(post);
              const cover = getCoverUrl(post);
              return (
                <Grid item xs={12} sm={6} md={4} key={post._id}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid #e8eaf0",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "box-shadow 0.2s, transform 0.2s",
                      "&:hover": {
                        boxShadow: "0 8px 32px rgba(28,37,65,0.13)",
                        transform: "translateY(-4px)",
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() => navigate(`/blog/${post.slug}`)}
                      sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "stretch" }}
                    >
                      {/* Cover image */}
                      <Box
                        sx={{
                          height: 210,
                          overflow: "hidden",
                          borderRadius: "12px 12px 0 0",
                          backgroundColor: "#eef0f4",
                          flexShrink: 0,
                        }}
                      >
                        {cover ? (
                          <Box
                            component="img"
                            src={cover}
                            alt={content.title || ""}
                            sx={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.3s", ".MuiCardActionArea-root:hover &": { transform: "scale(1.04)" } }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              Why Not Car Rental
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <CardContent sx={{ flex: 1, p: 2.5, display: "flex", flexDirection: "column" }}>
                        {/* Tags */}
                        {post.tags?.length > 0 && (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                            {post.tags.slice(0, 3).map((tag) => (
                              <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                sx={{
                                  backgroundColor: "#EE8013",
                                  color: "white",
                                  fontWeight: 600,
                                  fontSize: 10,
                                  height: 20,
                                  borderRadius: 1,
                                }}
                              />
                            ))}
                          </Stack>
                        )}

                        {/* Title */}
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          color="#1C2541"
                          gutterBottom
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            lineHeight: 1.35,
                          }}
                        >
                          {content.title || "Untitled"}
                        </Typography>

                        {/* Summary */}
                        {content.summary && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              lineHeight: 1.6,
                              flex: 1,
                            }}
                          >
                            {content.summary}
                          </Typography>
                        )}

                        {/* Date + Read more */}
                        <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(post.publishedAt)}
                          </Typography>
                          <Typography
                            variant="caption"
                            fontWeight="bold"
                            sx={{ color: "#EE8013", textDecoration: "underline" }}
                          >
                            {t("readMore", "Read more")} →
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>
    </ThemeProvider>
  );
};

export default Blog;
